import clsx from "clsx";
import TwoSidebars from "../../layouts/TwoSidebars/TwoSidebars";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import PageTitle from "../../components/PageTitle/PageTitle";
import ButtonGroup from "../../components/ButtonGroup/ButtonGroup";
import {
  MdAdd,
  MdCategory,
  MdCheck,
  MdDelete,
  MdEdit,
  MdOutlineContacts,
  MdOutlineNavigateBefore,
  MdOutlineNavigateNext,
  MdSettings,
} from "react-icons/md";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SettingsBlocks from "../../components/SettingsBlocks/SettingsBlocks";
import {
  ACCEPTED_ATTACHMENT_TYPES,
  BUTTON_STYLES,
  CONTACT_DEFAULT_ICONS,
  ICON_SIZES,
} from "../../helpers/constants";
import useMobile from "../../hooks/useMobile";

import useGeneralOptionsDropdownMenu from "../../hooks/useGeneralOptionsDropdownMenu";
import useAllTransactions from "src/hooks/useAllTransactions";
import { AnimatePresence } from "framer-motion";
import LoadingWithTextPage from "src/components/LoadingWithText/LoadingWithTextPage";
import { useQueryClient } from "@tanstack/react-query";
import {
  objectHasChanges,
  rgbaObjectToString,
  showNotification,
} from "src/helpers/utils";
import Icon from "src/components/Icon/Icon";
import TextInput from "src/components/TextInput/TextInput";
import TextAreaInput from "src/components/TextAreaInput/TextAreaInput";
import CollapsibleBlock from "src/components/CollapsibleBlock/CollapsibleBlock";
import InfoBlock from "src/components/InfoBlock/InfoBlock";
import SourcesContactCards from "./SourcesContactCards";
import ColorPicker from "src/components/ColorPicker/ColorPicker";
import IconSelector from "src/components/IconSelector/IconSelector";
import ChangesPendingToSave from "src/components/ChangesPendingToSave/ChangesPendingToSave";
import ConfirmDialog from "src/components/ConfirmDialog/ConfirmDialog";
import SourceAddContactDetailsModal, {
  ECONTACT_TYPE,
} from "./SourceAddContactDetailsModal";
import { filter, findIndex, map, merge, omit } from "lodash";
import useCollapsibles, {
  ESourcesPageMobileCollapsibles,
} from "src/hooks/useCollapsibles";
import { EButtonKind } from "src/components/Button/ButtonTypes";
import useSourcesStore from "src/store/sourcesStore";
import useCache from "src/hooks/useCache";

const EditSourcePage: React.FC = () => {
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const { t } = useTranslation();
  const { isMobile } = useMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const animationDirection = location.state?.animationDirection || "right";

  const generalOptionsDropdown = useGeneralOptionsDropdownMenu();

  const {
    isLoadingAllTransactions,
    getSourceCustomSettings,
    isLoadingCustomizedSources,
    sourceExists,
  } = useAllTransactions();
  const { clearCache } = useCache();

  const { source: sourceParam } = useParams();

  const isLoadingSomething = useMemo(
    () =>
      [!!isLoadingAllTransactions, !!isLoadingCustomizedSources]?.includes(
        true
      ),
    [isLoadingAllTransactions, isLoadingCustomizedSources]
  );

  useEffect(() => {
    if (!isLoadingSomething) {
      if (!sourceExists(sourceParam as string)) {
        navigate("/404", { replace: true });
      }
    }
  }, [isLoadingSomething, sourceParam]);

  const sourceData = useMemo(() => {
    return {
      source: sourceParam,
      settings: getSourceCustomSettings(sourceParam as string),
    };
  }, [sourceParam, getSourceCustomSettings, isLoadingSomething, location]);

  const queryClient = useQueryClient();

  const { addSource } = useSourcesStore();
  const [icon, setIcon] = useState<string>("");
  const [sourceName, setSourceName] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState<boolean>(false);
  const { allSources } = useAllTransactions();
  const [newImageToUpload, setNewImageToUpload] = useState<any>(null);
  const [textColor, setTextColor] = useState<any>("");
  const [bgColor, setBgColor] = useState<any>("");
  const [currentImage, setCurrentImage] = useState<any>(null);
  const [showAddContactDetailsModal, setShowAddContactDetailsModal] =
    useState<boolean>(false);
  const [address, setAddress] = useState<string>("");
  const [savedContactData, setSavedContactData] = useState<any[]>([]);

  const sourcesNavigation = () => {
    const currentIndex = findIndex(
      allSources,
      (cat: string) => cat === sourceData?.source
    );

    if (currentIndex !== -1) {
      const nextSource = allSources?.[currentIndex + 1] || null;
      const prevSource = allSources?.[currentIndex - 1] || null;

      return {
        nextSource,
        prevSource,
      };
    }

    return { nextSource: null, prevSource: null };
  };

  const hasContactsSupport =
    "contacts" in navigator && "ContactsManager" in window;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    collapsiblePreferences,
    updateCollapsible,
    isSourceMobileCollapsibleOpen,
    toggleSourcesMobileCollapsible,
  } = useCollapsibles();

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const importContact = useCallback(async () => {
    if (hasContactsSupport) {
      const props = await (navigator as any)?.contacts?.getProperties?.();

      const [contact] = await (navigator as any)?.contacts?.select(props, {
        multiple: false,
      });

      if (contact) {
        const { email = [], tel = [] } = contact;

        if (email.length) {
          setSavedContactData((prev: any) => [
            ...prev,
            {
              type: ECONTACT_TYPE.EMAIL,
              value: email[0], // primer correo
              name: "Correo electrónico",
              icon: CONTACT_DEFAULT_ICONS[ECONTACT_TYPE.EMAIL],
            },
          ]);
        }

        if (tel.length) {
          setSavedContactData((prev: any) => [
            ...prev,
            {
              type: ECONTACT_TYPE.PHONE,
              value: tel[0], // primer teléfono
              name: "Teléfono",
              icon: CONTACT_DEFAULT_ICONS[ECONTACT_TYPE.PHONE],
            },
          ]);
        }
      }
    }
  }, []);

  const hasChanges = useMemo(() => {
    return objectHasChanges({
      original: {
        icon: sourceData?.settings?.icon,
        name: sourceData?.source,
        color: sourceData?.settings?.color,
        bg: sourceData?.settings?.bg,
        contact: savedContactData,
        newImage: null,
        address: sourceData?.settings?.address,
      },
      edited: {
        icon,
        name: sourceName,
        color: textColor,
        bg: bgColor,
        contact: savedContactData,
        newImage: newImageToUpload,
        address,
      },
    });
  }, [
    sourceName,
    sourceData?.settings?.icon,
    sourceData?.source,
    sourceData?.settings?.color,
    sourceData?.settings?.bg,
    textColor,
    bgColor,
    savedContactData,
    icon,
    address,
    newImageToUpload,
  ]);

  const nada = useCallback(() => {
    const settings = getSourceCustomSettings(sourceParam as string);
    setNewImageToUpload(null);
    setSourceName(sourceParam || "");
    setBgColor(settings.bg);
    setTextColor(settings.color);
    setIcon(settings.icon);
    setAddress(settings?.address);
    setSavedContactData(settings?.contact);
    setCurrentImage(settings?.picture || null);
  }, [sourceParam, isLoadingSomething, refreshKey]);

  useEffect(() => {
    nada();
  }, [isLoadingSomething, sourceParam, refreshKey]);

  return (
    <>
      <TwoSidebars
        right={
          isLoadingSomething ? null : isMobile ? null : (
            <SettingsBlocks layout="vertical" columns={isMobile ? 2 : 1} />
          )
        }
        isLoading={isLoadingSomething}
      >
        <div
          className={clsx([
            "flex",
            "flex-col",
            "w-full",
            "gap-2",
            "md:gap-2",
            "h-full",
          ])}
        >
          <AnimatePresence>
            {isLoadingSomething ? (
              <LoadingWithTextPage />
            ) : (
              <>
                <PageTitle
                  icon={<MdSettings size={ICON_SIZES.MD} />}
                  title={t("settings_page.title")}
                  titleLink="/settings"
                  subtitle={[
                    {
                      text: "Orígenes y Destinos",
                      link: "/settings/sources",
                    },
                    {
                      text: "Editar",
                    },
                  ]}
                  isLoading={false}
                  rightSide={
                    <ButtonGroup
                      align="right"
                      gap={1}
                      buttons={[
                        {
                          icon: (
                            <MdOutlineNavigateBefore size={ICON_SIZES.MD} />
                          ),
                          className: isMobile ? BUTTON_STYLES.HEADING : "",
                          tooltip: t("common_tooltips.back"),
                          onClick: () => {
                            navigate("/settings/sources");
                          },
                        },
                        {
                          icon: (
                            <MdCheck
                              size={ICON_SIZES.MD}
                              className="text-current"
                            />
                          ),
                          tooltip: t("common_tooltips.update"),
                          className: isMobile
                            ? BUTTON_STYLES.HEADING_SAVE
                            : clsx([
                                "md:!text-lime-400",
                                "dark:md:!text-lime-400",
                              ]),
                          onClick: () => setShowUpdateDialog(true),
                          hide: !hasChanges,
                          kind: EButtonKind.NORMAL,
                        },
                        generalOptionsDropdown,
                      ]}
                    />
                  }
                />

                <div
                  className={clsx(["flex", "flex-col", "gap-4"])}
                  key={sourceParam}
                >
                  <motion.div
                    className={clsx([
                      "grid",
                      "grid-cols-2",
                      "w-full",
                      "gap-2",
                      "bg-white dark:bg-transparent",
                      "py-0 md:p-0",
                    ])}
                    initial={{
                      opacity: 0,
                      x: animationDirection === "left" ? -300 : 300,
                    }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{
                      opacity: 0,
                      x: animationDirection === "left" ? 300 : -300,
                    }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    <div
                      className={clsx([
                        "flex",
                        "rounded-lg",
                        "overflow-hidden",
                        "h-full",
                      ])}
                    >
                      <div
                        className={clsx([
                          "flex",
                          "w-full",
                          "h-full",
                          "flex-col",
                          "items-center",
                          "justify-center",
                          "p-4",
                          "md:py-10 md:p-6",
                          "rounded-lg",
                          "transition-all",
                          "gap-1",
                          "shadow-sm",
                          "!max-h-[180px]",
                          "!min-h-[180px]",
                          "md:!max-h-[240px]",
                          "md:!min-h-[240px]",
                        ])}
                        style={{
                          backgroundColor: rgbaObjectToString(bgColor),
                          color: rgbaObjectToString(textColor),
                        }}
                      >
                        {!icon ? (
                          <>
                            <MdCategory size={ICON_SIZES.XL * 2} />
                          </>
                        ) : (
                          <Icon name={icon as any} size={ICON_SIZES.XL * 2} />
                        )}
                        <div
                          className={clsx([
                            "text-center",
                            "capitalize",
                            "font-medium",
                            "text-sm",
                            "md:text-sm",
                            "p-2",
                            "truncate",
                            "max-w-full",
                          ])}
                        >
                          {sourceName}
                        </div>
                      </div>
                    </div>
                    <div
                      onClick={() => openFileDialog()}
                      className={clsx([
                        "flex",
                        "rounded-lg",
                        "cursor-pointer",
                        "h-full",
                        "overflow-hidden",
                      ])}
                    >
                      {newImageToUpload ? (
                        <img
                          src={URL.createObjectURL(newImageToUpload)}
                          className={clsx([
                            "flex",
                            "h-full",
                            "w-full",
                            "object-cover",
                            "rounded-lg",
                            "bg-checkers",
                            "!max-h-[180px]",
                            "!min-h-[180px]",
                            "md:!max-h-[240px]",
                            "md:!min-h-[240px]",
                          ])}
                        />
                      ) : (
                        <>
                          {currentImage ? (
                            <img
                              src={currentImage.thumbnailUrl}
                              alt={currentImage.originalName}
                              className={clsx([
                                "flex",
                                "h-full",
                                "w-full",
                                "object-cover",
                                "rounded-lg",
                                "bg-checkers",
                                "!max-h-[180px]",
                                "!min-h-[180px]",
                                "md:!max-h-[240px]",
                                "md:!min-h-[240px]",
                              ])}
                            />
                          ) : (
                            <div
                              className={clsx([
                                "flex",
                                "h-full",
                                "w-full",
                                "items-center",
                                "justify-center",
                                "text-gray-500",
                                "bg-checkers",
                                "flex-col",
                                "!max-h-[180px]",
                                "!min-h-[180px]",
                                "md:!max-h-[240px]",
                                "md:!min-h-[240px]",
                              ])}
                            >
                              <span
                                className={clsx([
                                  "backdrop-blur-sm",
                                  "md:text-base",
                                  "text-xs",
                                  "font-normal",
                                ])}
                              >
                                Sin imagen personalizada
                              </span>
                              <span
                                className={clsx([
                                  "backdrop-blur-sm",
                                  "md:text-base",
                                  "text-xs",
                                  "font-medium",
                                ])}
                              >
                                ¿Quieres agregar una?
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </motion.div>

                  <input
                    type="file"
                    multiple={false}
                    accept={ACCEPTED_ATTACHMENT_TYPES}
                    ref={fileInputRef}
                    onChange={async (
                      e: React.ChangeEvent<HTMLInputElement>
                    ) => {
                      // setIsLoading(true);

                      setNewImageToUpload(e?.target?.files?.[0]);
                      // await handleFileChange(e?.target?.files);
                      // setIsLoading(false);
                    }}
                    className="hidden"
                  />

                  <ButtonGroup
                    align="right"
                    buttons={[
                      {
                        icon: <MdOutlineNavigateBefore size={32} />,
                        onClick: () => {
                          navigate(
                            `/settings/sources/${
                              sourcesNavigation().prevSource
                            }`,
                            {
                              replace: true,
                              state: {
                                animationDirection: "left",
                              },
                            }
                          );
                        },
                        disabled:
                          !sourcesNavigation()?.prevSource || isUpdating,
                        hide: !sourcesNavigation()?.prevSource,
                        tooltip: t("common_tooltips.previous"),
                      },
                      {
                        icon: <MdOutlineNavigateNext size={32} />,
                        onClick: () => {
                          navigate(
                            `/settings/sources/${
                              sourcesNavigation().nextSource
                            }`,
                            {
                              replace: true,
                              state: {
                                animationDirection: "right",
                              },
                            }
                          );
                        },
                        disabled:
                          !sourcesNavigation()?.nextSource || isUpdating,
                        hide: !sourcesNavigation()?.nextSource,
                        tooltip: t("common_tooltips.next"),
                      },
                    ]}
                    gap={1}
                  />

                  <div
                    className={clsx(["flex", "flex-col", "gap-4", "w-full"])}
                  >
                    <TextInput
                      placeholder={t("Digita el nombre del Origen / Destino")}
                      onChange={(e: any) => setSourceName(e?.target?.value)}
                      value={sourceName}
                      label={t("Nombre")}
                      required
                    />
                    <TextAreaInput
                      value={address}
                      onChange={(e: any) => setAddress(e?.target?.value)}
                      autoComplete="off"
                      label="Dirección física"
                      rows={3}
                      placeholder="Dirección física del Origen / Destino"
                    />
                  </div>

                  <CollapsibleBlock
                    title="Datos de contacto"
                    open={
                      isMobile
                        ? isSourceMobileCollapsibleOpen(
                            ESourcesPageMobileCollapsibles.CONTACT_DATA
                          )
                        : collapsiblePreferences.sourcesContactDataOpen
                    }
                    onChange={(isOpen: boolean) => {
                      if (isMobile) {
                        toggleSourcesMobileCollapsible(
                          ESourcesPageMobileCollapsibles.CONTACT_DATA
                        );
                      } else {
                        updateCollapsible({
                          sourcesContactDataOpen: isOpen,
                        });
                      }
                    }}
                  >
                    <div
                      className={clsx([
                        "flex",
                        "w-full",
                        "flex-col",
                        "gap-4",
                        "relative",
                      ])}
                    >
                      <InfoBlock
                        message={`Agrega ${
                          savedContactData?.length ? "más " : ""
                        }datos de contacto a este perfil.`}
                        displayMobileIcon
                        buttons={[
                          {
                            text: isMobile ? "" : "Agregar",
                            icon: <MdAdd size={ICON_SIZES.MD} />,
                            onClick: () => setShowAddContactDetailsModal(true),
                          },
                          {
                            text: isMobile ? "" : "Importar",
                            icon: <MdOutlineContacts size={ICON_SIZES.MD} />,
                            onClick: () => importContact(),
                            hide: !hasContactsSupport,
                          },
                        ]}
                      />
                      {savedContactData?.length ? (
                        <SourcesContactCards
                          columns={2}
                          items={savedContactData}
                          onDelete={() => {}}
                          onEdit={() => {}}
                          // actions={[
                          //   {
                          //     icon: <MdDelete size={ICON_SIZES.MD} />,
                          //     kind: EButtonKind.DANGER,
                          //     onClick: async (index: number) => {
                          //       const newData = filter(
                          //         savedContactData,
                          //         (_el: any, key: number) => key !== index
                          //       );
                          //       console.log("Hola", index, savedContactData);

                          //       console.log({ newData });

                          //       // setIsUpdating(true);
                          //       await addSource({
                          //         params: {
                          //           source: sourceData?.source,
                          //           settings: {
                          //             ...sourceData?.settings,
                          //             contact: newData,
                          //           },
                          //           originalSource: sourceData?.source,
                          //         },
                          //       })
                          //         .then(async (res: any) => {
                          //           console.log({ res });
                          //           await clearCache();
                          //           showNotification({
                          //             message: t("Cambios guardados"),
                          //             kind: "success",
                          //           });
                          //           setRefreshKey((prev: number) => prev + 1);

                          //           setIsUpdating(false);
                          //           setShowUpdateDialog(false);
                          //         })
                          //         .catch((err) => {
                          //           showNotification({
                          //             message: t("Error guardando los cambios"),
                          //             kind: "error",
                          //           });
                          //           setIsUpdating(false);
                          //         });
                          //     },
                          //   },
                          //   {
                          //     icon: <MdEdit size={ICON_SIZES.MD} />,
                          //     kind: EButtonKind.EDIT,
                          //     onClick: () => {},
                          //   },
                          // ]}
                        />
                      ) : null}
                    </div>
                  </CollapsibleBlock>

                  <CollapsibleBlock title="Colores">
                    <div
                      className={clsx([
                        "grid",
                        "grid-cols-1",
                        "md:grid-cols-2",
                        "gap-2",
                      ])}
                    >
                      <ColorPicker
                        textColor={textColor}
                        onChange={(color: any) => {
                          setBgColor(color.rgb);
                        }}
                        color={bgColor}
                        label={t("Fondo")}
                        showText={false}
                      />
                      <ColorPicker
                        textColor={bgColor}
                        onChange={(color: any) => {
                          setTextColor(color.rgb);
                        }}
                        color={textColor}
                        label={t("Texto")}
                        showText={false}
                      />
                    </div>
                  </CollapsibleBlock>
                  <IconSelector
                    onClick={(icon) => setIcon(icon)}
                    background={rgbaObjectToString(bgColor)}
                    selected={icon}
                    color={rgbaObjectToString(textColor) || ""}
                  />

                  <div
                    className={clsx([
                      "flex",
                      "gap-4",
                      "flex-row",
                      "justify-between",
                      "items-center",
                    ])}
                  >
                    <ChangesPendingToSave show={hasChanges} />
                    <div className={clsx(["flex", "w-full", "justify-end"])}>
                      <ButtonGroup
                        fullWidth
                        gap={1}
                        buttons={[
                          {
                            text: sourceData
                              ? t("shared.update")
                              : t("shared.save"),
                            icon: <MdCheck size={32} />,
                            kind: EButtonKind.SAVE,
                            onClick: async () => {
                              setShowUpdateDialog(true);
                            },
                            disabled: isUpdating || !hasChanges,
                            isLoading: isUpdating,
                          },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </AnimatePresence>
        </div>
      </TwoSidebars>
      {showUpdateDialog && (
        <ConfirmDialog
          title={t("common_dialog_titles.update")}
          description={t("common_dialogs.save_changes")}
          onClose={() => {
            setShowUpdateDialog(false);
          }}
          isLoading={isUpdating}
          confirmKind={EButtonKind.SAVE}
          onConfirm={async () => {
            setIsUpdating(true);
            await addSource({
              params: {
                source: sourceName,
                settings: merge({}, sourceData?.settings, {
                  color: textColor,
                  bg: bgColor,
                  icon,
                  address,
                }),
                originalSource: sourceData?.source || sourceData,
                file: newImageToUpload,
              },
            })
              .then(async (res: any) => {
                setNewImageToUpload(null);
                await clearCache();
                showNotification({
                  message: t("Cambios guardados"),
                  kind: "success",
                });
                setRefreshKey((prev: number) => prev + 1);
                if (sourceData?.source !== sourceName) {
                  navigate(`/settings/sources/${sourceName}`, {
                    replace: true,
                  });
                }

                setIsUpdating(false);
                setShowUpdateDialog(false);
              })
              .catch((err) => {
                showNotification({
                  message: t("Error guardando los cambios"),
                  kind: "error",
                });
                setIsUpdating(false);
              });
          }}
        />
      )}
      {showAddContactDetailsModal && (
        <SourceAddContactDetailsModal
          isLoading={isUpdating}
          onClose={() => setShowAddContactDetailsModal(false)}
          onConfirm={async (data: any) => {
            setIsUpdating(true);
            await addSource({
              params: {
                source: sourceData?.source,
                settings: merge({}, sourceData?.settings, {
                  contact: [...savedContactData, data],
                }),
                originalSource: sourceData?.source,
              },
            })
              .then(async (res: any) => {
                await clearCache();
                setRefreshKey((prev: number) => prev + 1);
                showNotification({
                  message: t("Cambios guardados"),
                  kind: "success",
                });

                setIsUpdating(false);
                setShowAddContactDetailsModal(false);
              })
              .catch((err) => {
                showNotification({
                  message: t("Error guardando los cambios"),
                  kind: "error",
                });
                setIsUpdating(false);
              });
          }}
        />
      )}
    </>
  );
};

export default EditSourcePage;
